import styled from 'styled-components';
import { Card, Columns, Disclosure, Facts, Field, Heading, Small } from '../CreditWorkflowUI';
import { PanelBody, PanelHeader, PanelSubtitle, PanelTitle, WorkspaceButton, WorkspacePanel } from '../shared/CreditWorkspace';

// ABF review standard (reviewStyles / contractReviewStyles, PRs #92 and #93).
// Kept credit-local until the unmerged ABF foundations reach main.
export const INK = {
  strong: '#edf4ff', body: '#e5edf7', soft: '#c5d4e6', muted: '#a8b6ca',
  faint: '#91a4bb', accent: '#67c6f4', line: '#29374b', lineStrong: '#3d526a',
  panel: '#0c1524', raised: '#0e1a2b', well: '#142033',
};

export const Surface = styled.section`
  color: ${INK.body}; font-size: 14px; line-height: 1.55; min-width: 0;
  [hidden] { display: none !important; }
  h2, h3, p { margin: 0; }
  h2 { font-size: 17px; color: ${INK.strong}; }
  h3 { font-size: 15px; color: ${INK.strong}; }
  p { white-space: pre-wrap; overflow-wrap: anywhere; }
  button, input, select, textarea { font-family: inherit; }
  button:focus-visible, summary:focus-visible { outline: 2px solid ${INK.accent}; outline-offset: 3px; }
  ${Card} { padding: 18px; background: ${INK.panel}; border-color: ${INK.line}; border-radius: 14px; }
  ${Small} { color: ${INK.muted}; font-size: 13px; }
  ${Heading} h2 { font-size: 17px; }
  ${Columns} { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  ${Facts} {
    > div { border-color: ${INK.line}; background: ${INK.panel}; padding: 12px 14px; }
    strong { font-size: 17px; color: ${INK.strong}; }
  }
  ${WorkspacePanel} { background: ${INK.panel}; border-color: ${INK.line}; }
  ${PanelHeader} { background: ${INK.raised}; padding: 16px; }
  ${PanelTitle} { font-size: 17px; color: ${INK.strong}; }
  ${PanelSubtitle} { font-size: 13px; color: ${INK.muted}; }
  ${PanelBody} { font-size: 14px; }
  ${Disclosure} { background: ${INK.panel}; border-color: ${INK.line}; }
  ${Disclosure} summary { font-size: 14px; color: ${INK.soft}; }
  ${Field} { font-size: 13px; color: ${INK.soft}; }
  input:not([type='checkbox']), select, textarea {
    color: ${INK.body}; background: ${INK.well}; border-color: ${INK.lineStrong}; font-size: 14px;
  }
  ${WorkspaceButton} { min-height: 36px; font-size: 13px; }
  section[aria-label] { scroll-margin-top: 340px; }
  @media (max-width: 1050px), (max-height: 700px) {
    section[aria-label] { scroll-margin-top: 110px; }
  }
  @media (max-width: 1150px) { ${Columns} { grid-template-columns: 1fr; } }
`;

export const Journey = styled.ol`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); list-style: none;
  padding: 0; margin: 0 0 18px; border: 1px solid ${INK.line}; border-radius: 10px; overflow: hidden;
  li { display: flex; gap: 12px; align-items: center; padding: 15px 18px; background: #0c1626; }
  li + li { border-left: 1px solid ${INK.line}; }
  li[aria-current='step'] { background: #12273c; box-shadow: inset 0 -2px ${INK.accent}; }
  .step { display: grid; place-items: center; width: 30px; height: 30px; border: 1px solid ${INK.lineStrong}; border-radius: 50%; flex-shrink: 0; color: #b5c9dd; }
  strong { display: block; font-size: 14px; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 2px; }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    li + li { border-left: 0; border-top: 1px solid ${INK.line}; }
  }
`;

export const Strip = styled.dl`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin: 0 0 18px;
  > div { padding: 12px 14px; border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.panel}; min-width: 0; }
  dt { color: ${INK.faint}; font-size: 11px; font-weight: 650; letter-spacing: .08em; text-transform: uppercase; }
  dd { margin: 4px 0 0; color: ${INK.strong}; font-size: 17px; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 3px; }
  @media (max-width: 1000px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const Layout = styled.div`
  display: grid; grid-template-columns: 250px minmax(0, 1fr); gap: 20px; align-items: start;
  @media (max-width: 1200px) { grid-template-columns: 215px minmax(0, 1fr); gap: 14px; }
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;

export const Rail = styled.nav`
  border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.panel}; overflow: hidden;
  .heading { padding: 17px 15px; border-bottom: 1px solid ${INK.line}; }
  .heading p { color: ${INK.muted}; font-size: 12px; margin-top: 5px; }
  .tasks { padding: 7px; }
  @media (max-width: 1000px) { .tasks { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 480px) { .tasks { grid-template-columns: 1fr; } }
`;

export const TaskButton = styled.button`
  display: block; width: 100%; padding: 13px 12px; margin: 3px 0; text-align: left;
  border: 1px solid transparent; border-radius: 7px; background: transparent; color: ${INK.body}; cursor: pointer;
  &[aria-pressed='true'] { border-color: #5293bd; background: #19334a; }
  &:hover { background: #142236; }
  &[aria-pressed='true']:hover { background: #19334a; }
  .category { display: block; color: #a8bacd; font-size: 12px; margin-bottom: 4px; }
  strong { display: block; font-size: 14px; font-weight: 650; }
  .reason { display: block; color: #a6d8f5; margin-top: 5px; font-size: 12px; }
`;

export const Work = styled.section`
  min-width: 0; scroll-margin-top: 110px;
  .work-heading { display: flex; justify-content: space-between; align-items: start; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
  .work-heading p { color: ${INK.muted}; font-size: 13px; margin-top: 3px; }
  .editor { margin-bottom: 16px; }
`;

export const Pair = styled.div<{ $paired: boolean }>`
  display: grid; grid-template-columns: ${p => p.$paired ? 'minmax(0, 1.05fr) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  gap: 16px; align-items: start;
  > div { min-width: 0; }
  @media (max-width: 1150px) { grid-template-columns: 1fr; }
`;

export const Note = styled.div`
  padding: 14px 16px; border: 1px solid #36485e; border-radius: 8px; background: #101c2b;
  color: ${INK.soft}; font-size: 13px; margin-bottom: 16px;
  strong { color: ${INK.strong}; }
`;

export const DecisionBar = styled.footer`
  position: sticky; bottom: 12px; z-index: 20;
  display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap;
  margin-top: 20px; padding: 14px 18px; background: #142438;
  border: 1px solid #43617f; border-radius: 10px; box-shadow: 0 8px 32px #0006;
  .state { min-width: 0; }
  .state strong { display: block; color: ${INK.strong}; font-size: 14px; }
  .state span { display: block; color: #b6c7da; font-size: 13px; margin-top: 3px; }
  @media (max-width: 700px), (max-height: 700px) { position: static; }
`;
